import {
  ButtonERP,
  ButtonFilterERP,
  Checkbox,
  SearchInput,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IbanMovimientosParamsProps } from "api/CuentaBancaria.service"
import { ESTADO_CONCILIACION } from "api/Types/EstadoConciliacion"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { InputNumber } from "primereact/inputnumber"
import { InputSwitch } from "primereact/inputswitch"
import { Dispatch, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { backendDateFormat } from "utils/formatDate"
import {
  INITIAL_ESTADOS_CONCILIAR,
  RESET_ESTADOS_CONCILIAR,
  getEstadoLabels,
} from "../constants/constants"

export const FilterMenu = ({
  datePadre,
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
  refrescar,
}: {
  datePadre: [Date, Date | null]
  serviceQuery: Dispatch<any> // eslint-disable-line @typescript-eslint/no-explicit-any
  sortingCriteria?: SortingCriteria
  skipCount?: IbanMovimientosParamsProps["SkipCount"]
  maxResultCount?: IbanMovimientosParamsProps["MaxResultCount"]
  resetPagination?: () => void
  refrescar: number
}) => {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [estados, setEstados] = useState<{ [key: number | string]: boolean }>(
    INITIAL_ESTADOS_CONCILIAR
  )

  const [mostrarForzadas, setMostrarForzadas] = useState(false)
  const [keyword, setKeyword] = useState<string | undefined>()
  const [amount, setAmount] = useState<Record<string, number | undefined>>({
    min: undefined,
    max: undefined,
  })

  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (queryParams: Partial<IbanMovimientosParamsProps>) => {
    const estadosConciliaciones = Object.keys(estados)
      .filter((key) => estados[key])
      .map(Number)

    const asesoriaParams = {
      EsConciliacionForzada: mostrarForzadas,
      EstadosConciliaciones: estadosConciliaciones.length
        ? estadosConciliaciones
        : undefined,
      AmountLowerLimit: queryParams.AmountLowerLimit,
      AmountUpperLimit: queryParams.AmountUpperLimit,
      BookingDateLowerLimit: queryParams.BookingDateLowerLimit,
      BookingDateUpperLimit: queryParams.BookingDateUpperLimit,
      Keyword: queryParams.Keyword,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(asesoriaParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }
  const query = {
    EsConciliacionForzada: mostrarForzadas,
    AmountLowerLimit: amount?.min,
    AmountUpperLimit: amount?.max,
    BookingDateLowerLimit: backendDateFormat(datePadre[0]),
    BookingDateUpperLimit: backendDateFormat(
      datePadre[1]
        ? datePadre[1]
        : datePadre[0] <= new Date()
        ? new Date()
        : datePadre[0]
    ),
    SortingCriteria: sortingCriteria,
    SkipCount: skipCount,
    MaxResultCount: maxResultCount,
    Keyword: keyword,
  }
  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }

  const resetEstados = () => {
    setEstados(RESET_ESTADOS_CONCILIAR)
  }

  const toggleEstado = (key: number) => {
    setEstados((prevEstado: { [key: number | string]: boolean }) => ({
      ...prevEstado,
      [key]: !prevEstado[key], // Alterna el valor booleano
    }))
  }

  const appliedFiltersTags = [
    {
      label: `${t("nsAssets.filters.status.title")}:`,
      value: estados[ESTADO_CONCILIACION.SIN_CONCILIAR]
        ? getEstadoLabels(t)[ESTADO_CONCILIACION.SIN_CONCILIAR]
        : undefined,
      action: () => toggleEstado(ESTADO_CONCILIACION.SIN_CONCILIAR),
    },
    {
      label: `${t("nsAssets.filters.status.title")}:`,
      value: estados[ESTADO_CONCILIACION.CONCILIADO]
        ? getEstadoLabels(t)[ESTADO_CONCILIACION.CONCILIADO]
        : undefined,
      action: () => toggleEstado(ESTADO_CONCILIACION.CONCILIADO),
    },
    {
      label: `${t("nsAssets.filters.status.forzosas")}`,
      value: mostrarForzadas ?? undefined,
      action: () => setMostrarForzadas(false),
    },
    {
      label: t("filtros.appliedTag.amountFrom"),
      value: amount?.min
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.min)
        : undefined,
      action: () => setAmount({ ...amount, min: 0 }),
    },
    {
      label: t("filtros.appliedTag.amountTo"),
      value: amount?.max
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.max)
        : undefined,
      action: () => setAmount({ ...amount, max: 0 }),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    if (!datePadre[1]) return
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    skipCount,
    maxResultCount,
    datePadre,
    appliedFilters?.length,
    refrescar,
    amount?.min,
    amount?.max,
    sortingCriteria?.property,
    sortingCriteria?.sort,
  ])

  const handleResetFilters = () => {
    resetEstados()
    setAmount({ min: undefined, max: undefined })
    setMostrarForzadas(false)
  }

  return (
    <>
      <div className='!tw-px-6 tw-flex tw-flex-col lg:tw-flex-row tw-justify-end tw-items-center tw-py-4 tw-shadow-3xl mb-1 tw-bg-white'>
        <div className='tw-flex tw-flex-row tw-w-full tw-justify-between'>
          <div className='tw-flex tw-flex-row tw-w-fit tw-items-center tw-text-lg tw-font-medium tw-text-ellipsis tw-whitespace-nowrap'>
            <span className='tw-w-fit tw-whitespace-nowrap tw-mr-2'>
              {t("bancos.conciliacion.modal.movimientosBancarios")}
            </span>
          </div>
          <div className='tw-grid tw-gap-1 lg:tw-gap-3 lg:tw-flex lg:tw-flex-row md:tw-grid-cols-1 tw-items-center tw-w-fit'>
            <SearchInput
              placeholder={`${t("bancos.conciliacion.searchPlaceholder")}`}
              onChange={(ev: string) => {
                setKeyword(ev)
              }}
              inputValue={keyword}
              className='tw-h-10 tw-w-full sm:tw-w-72'
            />

            <ButtonFilterERP
              isActive={appliedFilters.length > 0}
              numberActive={appliedFilters?.length}
              onClick={openFilters}
              deleteFilter={handleResetFilters}
              filterButtonLabel={`${t("filtros.title")}`}
            />
          </div>
        </div>
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "575px": "100vw" }}
          className='!tw-m-0 !tw-h-full tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonERP
                customize={{
                  type: "primary",
                  size: "md",
                }}
                className='!tw-w-full'
                onClick={handleApplyFilters}
                label={`${t("filtros.labelApply")}`}
              />
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("tickets.filter.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("tickets.filter.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={filter.label}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("nsAssets.filters.status.titlePlural")}
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='conciliacion-conciliado'
                className='tw-mr-4'
                isChecked={estados[ESTADO_CONCILIACION.CONCILIADO]}
                onClick={() => toggleEstado(ESTADO_CONCILIACION.CONCILIADO)}
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() => toggleEstado(ESTADO_CONCILIACION.CONCILIADO)}
              >
                {t("nsAssets.filters.status.conciliado")}
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='conciliacion-sin-conciliar'
                className='tw-mr-4'
                isChecked={estados[ESTADO_CONCILIACION.SIN_CONCILIAR]}
                onClick={() => toggleEstado(ESTADO_CONCILIACION.SIN_CONCILIAR)}
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() => toggleEstado(ESTADO_CONCILIACION.SIN_CONCILIAR)}
              >
                {t("nsAssets.filters.status.sinConciliar")}
              </span>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("nsAssets.filters.status.forzosas")}
              </span>
            </div>
            <div className='tw-w-full tw-flex tw-flex-col tw-justify-between tw-items-center mobile:tw-flex-row tw-mb-6'>
              <span>{t("nsAssets.filters.status.showForzosas")}</span>
              <InputSwitch
                checked={mostrarForzadas}
                onChange={() => setMostrarForzadas(!mostrarForzadas)}
              />
            </div>
          </div>
          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("nsAssets.filters.import.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-8'>
              <div className='tw-flex tw-flex-col'>
                <label>
                  {t("bancos.conciliacion.filterMenu.amountLabelSince")}
                </label>
                <InputNumber
                  className='[&_input]:!tw-w-36 !tw-w-36'
                  value={amount.min}
                  placeholder='0,00 €'
                  onValueChange={(e) => setAmount({ ...amount, min: e.value })}
                  minFractionDigits={2}
                  min={0}
                  max={amount?.max ?? Number.MAX_SAFE_INTEGER}
                  mode='currency'
                  currency='EUR'
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label>
                  {t("bancos.conciliacion.filterMenu.amountLabelUntil")}
                </label>
                <InputNumber
                  className='[&_input]:!tw-w-36 !tw-w-36'
                  value={amount?.max}
                  placeholder='0,00 €'
                  onValueChange={(e) => {
                    setAmount({ ...amount, max: e.value })
                  }}
                  minFractionDigits={2}
                  min={amount?.min ?? 0}
                  max={Number.MAX_SAFE_INTEGER}
                  mode='currency'
                  currency='EUR'
                />
              </div>
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
